import React, { forwardRef, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Divider,
  ThemeProvider,
  createTheme,
  Button,
  CircularProgress,
} from "@mui/material";
import { Download, Image as ImageIcon } from "@mui/icons-material";
import { useSchools } from "hooks/useSchools";
import { useAuth } from "contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import { convertDateToHindiWords, convertDateToEnglishWords } from "utils/dateToWordsHindi";
import { useTeacher } from "hooks/useTeacher";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";
import { useMarkSheets } from "hooks/useMarkSheets";
import { TcDataResponseDto } from "hooks/useTdData";

// ✅ Print-friendly theme
const theme = createTheme({
  typography: {
    fontFamily: "Times New Roman, Georgia, serif",
  },
});

const TcTemplate = forwardRef<
  HTMLDivElement,
  { student?: any; hidePrintButton?: boolean; tcData?: TcDataResponseDto | null }
>(({ student = {}, hidePrintButton = false, tcData = null }, ref) => {
  // ---------- Student + school data ----------
  const studentData = student?.student || student;
  const marksheetClassId =
    (studentData as any)?.schoolClassId ??
    (studentData as any)?.schoolClass?.id ??
    (student as any)?.schoolClassId ??
    (student as any)?.schoolClass?.id;
  const marksheetSessionId =
    (studentData as any)?.sessionId ??
    (studentData as any)?.session?.id ??
    (student as any)?.sessionId ??
    (student as any)?.session?.id;
  const { useSchoolById, useSchoolLogoInfo } = useSchools();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const schoolId = user?.schoolId ?? undefined;
  const { data: currentSchool } = useSchoolById(schoolId);

  // ---------- Teacher data ----------
  const { teachersQuery } = useTeacher(schoolId);
  const teachers = teachersQuery.data || [];

  // ---------- Marksheet data ----------
  const { marksheetsQuery } = useMarkSheets(
    false,
    false,
    marksheetClassId,
    marksheetSessionId
  );
  const marksheets = marksheetsQuery.data || [];

  // Refetch marksheets when student data changes to ensure latest data
  const studentId = studentData?.id || (student as any)?.id || (student as any)?.studentId;
  useEffect(() => {
    if (studentId) {
      marksheetsQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  // ---------- Translation ----------
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  // ---------- Download + logo states ----------
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [schoolLogoUrl, setSchoolLogoUrl] = useState<string | null>(null);
  const { data: logoInfo, isLoading: logoLoading } =
    useSchoolLogoInfo(schoolId);

  // ---------- Logo fetching with cache ----------
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        if (logoInfo && logoInfo.fileName) {
          const url = `${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`;
          setSchoolLogoUrl(url);
          localStorage.setItem(`school_logo_${schoolId}`, url);
          return;
        }
        const cached = localStorage.getItem(`school_logo_${schoolId}`);
        if (cached) {
          setSchoolLogoUrl(cached);
          return;
        }
        setSchoolLogoUrl("/logo.png");
      } catch (err) {
        setSchoolLogoUrl("/logo.png");
      }
    };
    if (schoolId && !logoLoading) fetchLogo();
  }, [schoolId, logoInfo, logoLoading]);

  // ---------- Helpers ----------
  const get = (key: string, fallback = "") => {
    const value =
      (studentData as any)[key] || (student as any)[key] || fallback;
    if (typeof value === "string") return value.toUpperCase();
    if (typeof value === "number") return value.toString();
    if (typeof value === "boolean") return value ? "YES" : "NO";
    if (value && typeof value === "object" && value.name)
      return value.name.toUpperCase();
    return fallback;
  };

  // Get class teacher from student's class information
  const getClassTeacherName = () => {
    const studentClassName = get("className");
    if (!studentClassName || !teachers.length) return "Class Teacher Name";
    
    // Try to find teacher assigned to this class
    const classTeacher = teachers.find((teacher) => {
      return teacher.firstName && teacher.lastName;
    });
    
    if (classTeacher) {
      return `${classTeacher.firstName} ${classTeacher.lastName}`.toUpperCase();
    }
    
    return "Class Teacher Name";
  };

  const formatDate = (dateArray: number[] | string) => {
    if (Array.isArray(dateArray) && dateArray.length >= 3) {
      const [year, month, day] = dateArray;
      return `${day.toString().padStart(2, "0")}/${month
        .toString()
        .padStart(2, "0")}/${year}`;
    }
    return typeof dateArray === "string" ? dateArray.toUpperCase() : "";
  };

  const getDateInHindiWords = (dateArray: number[] | string) => {
    if (Array.isArray(dateArray) && dateArray.length >= 3) {
      const [year, month, day] = dateArray;
      const dateString = `${day}/${month}/${year}`;
      return convertDateToHindiWords(dateString);
    }
    return "";
  };

  const getDateInWords = (dateArray: number[] | string) => {
    if (Array.isArray(dateArray) && dateArray.length >= 3) {
      const [year, month, day] = dateArray;
      const dateString = `${day}/${month}/${year}`;
      return isEnglish ? convertDateToEnglishWords(dateString) : convertDateToHindiWords(dateString);
    }
    return "";
  };

  const formatGender = (gender: string) => {
    if (gender?.toLowerCase() === "m") return "MALE";
    if (gender?.toLowerCase() === "f") return "FEMALE";
    return gender?.toUpperCase() || "";
  };

  // Helper to get value or underline placeholder
  const getValueOrUnderline = (value: string | undefined | null | number): string => {
    if (value === undefined || value === null || value === "" || (typeof value === "string" && value.trim() === "")) {
      return "___________________";
    }
    return String(value);
  };

  // Helper to safely get student field value
  const getStudentField = (fieldName: string): string => {
    const value = (studentData as any)?.[fieldName] || (student as any)?.[fieldName] || (student as any)?.student?.[fieldName] || "";
    if (typeof value === "string" && value.trim()) {
      return value.toUpperCase();
    }
    if (typeof value === "number") {
      return String(value);
    }
    if (value && typeof value === "object" && value.name) {
      return String(value.name).toUpperCase();
    }
    return "";
  };

  const formatBoldValue = (value: string | undefined | null): string => {
    // If the value exists and is not empty, show it in bold uppercase
    if (value != null && typeof value === "string" && value.trim() !== "") {
      return `<strong>${value.toUpperCase()}</strong>`;
    }
    // Otherwise, show underline (not bold)
    return "___________________";
  };

// ---------- Download: Image ----------
const downloadAsImage = async () => {
  if (!ref || typeof ref === "function") return;
  const element = ref.current;
  if (!element) return;

  setIsDownloadingImage(true);
  try {
    await new Promise((r) => setTimeout(r, 100));

    // Capture from a detached clone so responsive wrappers never affect output.
    const waitForImages = async (rootEl: HTMLElement) => {
      const imgs = Array.from(rootEl.querySelectorAll("img"));
      await Promise.all(
        imgs.map(
          (img) =>
            new Promise<void>((res) => {
              if ((img as HTMLImageElement).complete) return res();
              img.addEventListener("load", () => res(), { once: true });
              img.addEventListener("error", () => res(), { once: true });
            })
        )
      );
    };

    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-10000px";
    container.style.top = "0";
    container.style.background = "#ffffff";
    container.style.padding = "0";
    container.style.margin = "0";
    container.style.zIndex = "-1";

    const cloned = element.cloneNode(true) as HTMLElement;
    cloned.style.margin = "0";
    cloned.style.transform = "none";
    cloned.style.boxShadow = "none";
    cloned.style.width = "210mm";
    cloned.style.maxWidth = "210mm";
    container.appendChild(cloned);
    document.body.appendChild(container);

    try {
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await waitForImages(cloned);

      const rect = cloned.getBoundingClientRect();
      const originalCanvas = await html2canvas(cloned, {
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: true,
        logging: false,
        scale: 2,
        width: rect.width,
        height: rect.height,
        windowWidth: rect.width,
        windowHeight: rect.height,
        scrollX: 0,
        scrollY: 0,
      } as any);

      // Step 2: Define desired margin in pixels
      const marginPx = 60; // Adjust: more = bigger white gap

      // Step 3: Create a new larger canvas for padding
      const paddedCanvas = document.createElement("canvas");
      paddedCanvas.width = originalCanvas.width + marginPx * 2;
      paddedCanvas.height = originalCanvas.height + marginPx * 2;

      const ctx = paddedCanvas.getContext("2d");
      if (!ctx) return;

      // Step 4: Fill white background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);

      // Step 5: Draw original image in center with margins
      ctx.drawImage(originalCanvas, marginPx, marginPx);

      // Step 6: Convert to image and trigger download
      const imgData = paddedCanvas.toDataURL("image/png", 1.0);
      const a = document.createElement("a");
      a.download = `tc-${studentData?.studentName || "student"}.png`;
      a.href = imgData;
      a.click();
    } finally {
      container.remove();
    }
  } catch (err) {
  } finally {
    setIsDownloadingImage(false);
  }
};

  // ---------- Download: PDF ----------
const downloadAsPdfFromImage = async () => {
  let element: any = ref && typeof ref !== "function" ? ref.current : null;
  if (!element) element = document.querySelector(".tc-template");
  if (!element) {
    alert("Template not ready for download.");
    return;
  }

  setIsDownloadingPdf(true);
  try {
    await new Promise((r) => setTimeout(r, 80));

    // Render the full template to a high-res canvas
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
    } as any);

    const imgData = canvas.toDataURL("image/jpeg", 1.0);

    // Create a single-page A4 PDF
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageWidth = 210;  // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const margin = 10;      // uniform margin (mm) for all sides

    // Fit image inside available area (page minus margins)
    const availableWidth = pageWidth - margin * 2;
    const availableHeight = pageHeight - margin * 2;

    // Preserve aspect ratio
    let renderWidth = availableWidth;
    let renderHeight = (canvas.height / canvas.width) * renderWidth;

    if (renderHeight > availableHeight) {
      renderHeight = availableHeight;
      renderWidth = (canvas.width / canvas.height) * renderHeight;
    }

    // Center image inside the page with equal margins
    const offsetX = (pageWidth - renderWidth) / 2;
    const offsetY = (pageHeight - renderHeight) / 2;

    pdf.addImage(
      imgData,
      "JPEG",
      offsetX,
      offsetY,
      renderWidth,
      renderHeight,
      undefined,
      "FAST"
    );

    pdf.save(`tc-${studentData?.studentName || "student"}.pdf`);
  } catch (err) {
  } finally {
    setIsDownloadingPdf(false);
  }
};

  // ---------- Component ----------
  return (
    <ThemeProvider theme={theme}>
      <style>{`
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4 portrait; margin: 0; }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 210mm !important;
              height: 297mm !important;
              overflow: visible !important;
            }
            .tc-template {
              width: 210mm !important;
              min-height: 297mm !important;
              height: auto !important;
              transform: none !important;
              border: none !important;
              box-shadow: none !important;
              display: block !important;
              overflow: visible !important;
              box-sizing: border-box !important;
            }
          }
        `}</style>

      <Box
        sx={{
          p: 0,
          pt: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          // Mobile: prevent page-level horizontal scroll; only the TC sheet scrolls.
          overflowX: { xs: "hidden", sm: "visible" },
        }}
      >
        <Box
          sx={{
            overflowX: { xs: "auto", sm: "visible" },
            WebkitOverflowScrolling: "touch",
            width: "100%",
            // Desktop: keep centered (same layout as before)
            display: { xs: "block", sm: "flex" },
            justifyContent: { sm: "center" },
          }}
        >
          {/* ===== Paper with fixed border and no gap ===== */}
          <Paper
            ref={ref}
            className="tc-template"
            sx={{
              width: "210mm",
              minHeight: "296.8mm",
              margin: "0 auto",
              backgroundColor: "#ffffff",
              border: "3px solid #000",
              boxShadow: "none",
              overflow: "hidden",
              position: "relative",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              padding: 0,
            }}
          >
          {/* Inner container for content fit */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              backgroundColor: "transparent",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between", // evenly spread content vertically
              height: "100%",
              paddingBottom: "4mm",
            }}
          >
            {/* ===== Vertical gradient overlay ===== */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 0,
                pointerEvents: "none",
                background:
                  "linear-gradient(to right, rgba(144,238,144,0.08) 0%, rgba(255,255,153,0.08) 50%, rgba(255,160,160,0.08) 100%)",
              }}
            />

            {/* ===== Transparent school logo background watermark ===== */}
            {schoolLogoUrl && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 0,
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={schoolLogoUrl}
                  alt="School Logo Background"
                  sx={{
                    width: "60%",
                    height: "auto",
                    opacity: 0.2,
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}

            {/* ===== Main certificate content ===== */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              {/* ===== DISE Code - Top Left ===== */}
              <Typography
                sx={{
                  position: "absolute",
                  top: "2mm",
                  left: "4mm",
                  fontSize: 15,
                  color: "#2c3e50",
                  fontWeight: 600,
                }}
              >
                DISE CODE: {currentSchool?.diseCode || "2323410"}
              </Typography>

              {/* ===== School Code - Top Right ===== */}
              <Typography
                sx={{
                  position: "absolute",
                  top: "2mm",
                  right: "4mm",
                  fontSize: 15,
                  color: "#2c3e50",
                  fontWeight: 600,
                }}
              >
                SCHOOL CODE: {currentSchool?.schoolCode || "541067"}
              </Typography>

              {/* ===== School Header with Logo ===== */}
              <Box sx={{ textAlign: "center", py: 0.5, position: "relative" }}>

                <Typography
                  sx={{
                    fontSize: 28,
                    fontWeight: "bold",
                    color: "#2c3e50",
                    letterSpacing: 0.3,
                    mt: 4, // Add margin top to move school name down
                    mb: 0.3,
                    textTransform: "uppercase",
                    display: "inline-block",
                  }}
                >
                  {currentSchool?.nameofSchool?.toUpperCase() ||
                    "Name of the School"}
                </Typography>
                
                {currentSchool?.district && (
                  <Typography
                    sx={{
                      fontSize: 15,
                      color: "#34495e",
                      mb: 0.4,
                      fontWeight: 600,
                    }}
                  >
                    District: {currentSchool.district.toUpperCase()}
                  </Typography>
                )}
                
                <Box
                  sx={{
                    width: "60%",
                    height: "1px",
                    background:
                      "linear-gradient(to right, transparent, #2c3e50, transparent)",
                    margin: "0 auto",
                    mb: 1,
                  }}
                />
                
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: "bold",
                    color: "#2c3e50",
                    mb: 0.3,
                    textTransform: "uppercase",
                  }}
                >
                  * {t("transfer_certificate")} *
                </Typography>
                
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#7f8c8d",
                    mb: 0.6,
                    fontStyle: "italic",
                  }}
                >
                  (शिक्षा अधिनियम के तहत जारी)
                </Typography>
                
                {/* Declaration Paragraph */}
                <Box
                  sx={{
                    width: "98%",
                    mx: "auto",
                    mt: 1,
                    mb: 1,
                    fontSize: 12,
                    lineHeight: 1.6,
                    color: "#2c3e50",
                    paddingLeft: "8px",
                    textAlign: "left",
                    "& strong": {
                      textDecorationThickness: "1px",
                      textUnderlineOffset: "2px",
                      textDecorationSkipInk: "auto",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontSize: 17,
                      lineHeight: 1.8,
                      whiteSpace: "pre-wrap",
                    }}
                    component="div"
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        // Get values
                        const studentNameValue = getStudentField("studentName");
                        const fatherNameValue = getStudentField("fatherName");
                        const motherNameValue = getStudentField("motherName");
                        const admissionDateValue = formatDate(studentData?.admissionDate || (student as any)?.admissionDate);
                        const religionValue = getStudentField("religion");
                        const classNameValue = getStudentField("className") || (studentData as any)?.schoolClass?.name || (student as any)?.schoolClass?.name;
                        const sessionYearValue = (studentData as any)?.session?.name || studentData?.sessionName || (student as any)?.session?.name || (student as any)?.sessionName;
                        const dateOfBirthValue = formatDate(studentData?.dateOfBirth || (student as any)?.dateOfBirth);
                        const dateOfBirthWordsValue = getDateInWords(studentData?.dateOfBirth || (student as any)?.dateOfBirth);

                        // Get TC data values - prioritize TC data from API response
                        const dateOfLeavingValue = tcData?.dateOfLeaving || "";
                        const behaviourValue = tcData?.behaviour || "";
                        const nextClassValue = tcData?.nextClass || "";
                        const resultClassValue = tcData?.resultClass || classNameValue;
                        const resultYearValue = tcData?.yearOfResult || sessionYearValue;
                        const resultValue = tcData?.result || "";
                        const nextSchoolDetailsValue = tcData?.nextSchoolDetails || "";
                        const nextSchoolStatusValue = tcData?.nextSchoolStatus || "";
                        
                        // Wrap all values in <strong> - Use TC data when available
                        const values: Record<string, string> = {
                          studentName: formatBoldValue(getStudentField("studentName")),
                          fatherName: formatBoldValue(getStudentField("fatherName")),
                          motherName: formatBoldValue(getStudentField("motherName")),
                          scholarNo: formatBoldValue(getStudentField("scholarNo")),
                          admissionDate: formatBoldValue(formatDate(studentData?.admissionDate || (student as any)?.admissionDate)),
                          transferFromSchool: formatBoldValue(isEnglish ? currentSchool?.nameofSchool : ""),
                          leavingDate: formatBoldValue(dateOfLeavingValue),
                          character: formatBoldValue(behaviourValue),
                          religion: formatBoldValue(getStudentField("religion")),
                          className: formatBoldValue(resultClassValue),
                          sessionYear: formatBoldValue(resultYearValue),
                          dateOfBirth: formatBoldValue(formatDate(studentData?.dateOfBirth || (student as any)?.dateOfBirth)),
                          dateOfBirthWords: formatBoldValue(getDateInWords(studentData?.dateOfBirth || (student as any)?.dateOfBirth)),
                          promotionClass: formatBoldValue(nextClassValue),
                          result: formatBoldValue(resultValue),
                          nextSchoolDetails: formatBoldValue(nextSchoolDetailsValue),
                          nextSchoolStatus: formatBoldValue(nextSchoolStatusValue),
                        };

                        // Replace placeholders in the translation text
                        let declarationText = t("tc_declaration");

                        declarationText = declarationText
                          .replace(/\{studentName\}/g, values.studentName)
                          .replace(/\{fatherName\}/g, values.fatherName)
                          .replace(/\{motherName\}/g, values.motherName)
                          .replace(/\{dateOfBirth\}/g, values.dateOfBirth)
                          .replace(/\{dateOfBirthWords\}/g, values.dateOfBirthWords)
                          .replace(/\{scholarNo\}/g, values.scholarNo)
                          .replace(/\{promotionClass\}/g, values.promotionClass)
                          .replace(/\{admissionDate\}/g, values.admissionDate);

                        return declarationText;
                      })(),
                    }}
                  />
                </Box>
                
                {/* <Box
                  sx={{
                    width: "80%",
                    height: "1px",
                    background:
                      "linear-gradient(to right, transparent, #bdc3c7, transparent)",
                    margin: "0 auto",
                    mb: 0.3,
                  }}
                /> */}
              </Box>

              {/* ===== Student Details Section ===== */}
              <Box
                sx={{
                  px: 1,
                  py: 0.4,
                  position: "relative",
                  zIndex: 1,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: "bold",
                    mb: 0.4,
                    color: "#2c3e50",
                    letterSpacing: 0.2,
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  * {t("student_details")} *
                </Typography> */}
               

                {/* ===== Student Table ===== */}
                <TableContainer
                  className="no-break-before"
                  sx={{
                    width: "100%",
                    overflow: "visible",
                  }}
                >
                  <Table 
                    sx={{ 
                      width: "100%", 
                      fontSize: 16, 
                      color: "#2c3e50",
                      tableLayout: "fixed",
                      "& .MuiTableCell-root": {
                        verticalAlign: "top",
                      }
                    }}
                  >
                    <TableBody>
                      {(() => {
                        const items: any[] = [
                          [t("tc_enrollment_no"), get("enrolmentNo") || (studentData as any)?.enrolmentNo || (student as any)?.enrolmentNo || (student as any)?.student?.enrolmentNo || ""],
                          [t("tc_gender"), formatGender(studentData?.gender)],
                          [t("tc_caste"), get("caste")],
                          [t("tc_religion"), get("religion")],
                          [t("tc_sub_caste"), get("subCaste")],
                          [t("tc_caste_certificate_no"), get("casteCertificate")],
                          [t("tc_sssmid"), get("sssmid")],
                          [t("tc_pen_no"), get("penNo")],
                          [t("tc_appar_id_no"), get("apparId")],
                          [t("tc_aadhar_no"), get("aadharNo")],
                          [t("tc_house_no"), get("houseNo")],
                          [t("tc_street"), get("street")],
                          [t("tc_landmark"), get("landmark")],
                          [t("tc_area"), get("area")],
                          [t("tc_village"), get("city")],
                          [t("tc_post_office"), get("postOffice")],
                          [t("tc_state"), get("state")],
                          [t("tc_pin_code"), get("pinCode")],
                          [t("tc_mobile_no"), get("mobileNo")],
                          [t("tc_bank_name"), get("bankName")],
                          [t("tc_ifc_code"), get("ifcCode")],
                          [t("tc_ac_no"), get("accountNo")],
                          [t("tc_annual_income"), get("annualIncome")],
                          [t("tc_income_certificate_no"), get("incomeCertificateNo")],
                          [t("tc_email"), get("gmail").toLowerCase()],
                        ];
                        const rows: React.ReactNode[] = [];
                        for (let i = 0; i < items.length; i += 2) {
                          const left = items[i];
                          const right = items[i + 1];
                          rows.push(
                            <TableRow key={`row-${i}`} sx={{ verticalAlign: "top" }}>
                              {/* Left item */}
                              <TableCell
                                sx={{
                                  border: "none",
                                  py: 0.5,
                                  px: 1,
                                  fontWeight: "bold",
                                  fontSize: 16,
                                  color: "#2c3e50",
                                  width: "26%",
                                  maxWidth: "26%",
                                  verticalAlign: "top",
                                  wordBreak: "normal",
                                  overflowWrap: "normal",
                                  lineHeight: 1.4,
                                  whiteSpace: "normal",
                                  boxSizing: "border-box",
                                  overflow: "hidden",
                                }}
                              >
                                {left?.[0]}
                              </TableCell>
                              <TableCell
                                sx={{
                                  border: "none",
                                  py: 0.5,
                                  px: 0.5,
                                  fontWeight: "bold",
                                  fontSize: 16,
                                  color: "#2c3e50",
                                  width: "1%",
                                  maxWidth: "1%",
                                  verticalAlign: "top",
                                  lineHeight: 1.4,
                                  whiteSpace: "nowrap",
                                  boxSizing: "border-box",
                                }}
                              >
                                :
                              </TableCell>
                              <TableCell
                                sx={{
                                  border: "none",
                                  py: 0.5,
                                  px: 1,
                                  fontSize: 16,
                                  color: "#34495e",
                                  width: "20%",
                                  maxWidth: "20%",
                                  // borderBottom: "1px solid #e9ecef",
                                  verticalAlign: "top",
                                  wordBreak: "break-word",
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                  lineHeight: 1.4,
                                  boxSizing: "border-box",
                                  overflow: "visible",
                                  wordSpacing: "normal",
                                }}
                              >
                                {left?.[1]}
                              </TableCell>

                              {/* Spacer between left and right groups */}
                              <TableCell sx={{ border: "none", width: "2%", maxWidth: "2%" }} />

                              {/* Right item */}
                              {right ? (
                                <>
                                  <TableCell
                                    sx={{
                                      border: "none",
                                      py: 0.5,
                                      px: 1,
                                      fontWeight: "bold",
                                      fontSize: 16,
                                      color: "#2c3e50",
                                      width: "26%",
                                      maxWidth: "26%",
                                      verticalAlign: "top",
                                      wordBreak: "normal",
                                      overflowWrap: "normal",
                                      lineHeight: 1.4,
                                      whiteSpace: "normal",
                                      boxSizing: "border-box",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {right?.[0]}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      border: "none",
                                      py: 0.5,
                                      px: 0.5,
                                      fontWeight: "bold",
                                      fontSize: 16,
                                      color: "#2c3e50",
                                      width: "1%",
                                      maxWidth: "1%",
                                      verticalAlign: "top",
                                      lineHeight: 1.4,
                                      whiteSpace: "nowrap",
                                      boxSizing: "border-box",
                                    }}
                                  >
                                    :
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      border: "none",
                                      py: 0.5,
                                      px: 1,
                                      fontSize: 16,
                                      color: "#34495e",
                                      width: "20%",
                                      maxWidth: "20%",
                                      // borderBottom: "1px solid #e9ecef",
                                      verticalAlign: "top",
                                      wordBreak: "break-word",
                                      wordWrap: "break-word",
                                      overflowWrap: "break-word",
                                      lineHeight: 1.4,
                                      boxSizing: "border-box",
                                      overflow: "visible",
                                      wordSpacing: "normal",
                                    }}
                                  >
                                    {right?.[1]}
                                  </TableCell>
                                </>
                              ) : (
                                <TableCell sx={{ border: "none", verticalAlign: "top" }} colSpan={3} />
                              )}
                            </TableRow>
                          );
                        }
                        return rows;
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* <Box
                  sx={{
                    width: "60%",
                    height: "1px",
                    background:
                      "linear-gradient(to right, transparent, #2c3e50, transparent)",
                    margin: "0 auto",
                    mb: 1,
                    mt: 1,
                  }}
                /> */}

                {/* ===== Remarks & Academic Details Heading ===== */}
                {/* <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: "bold",
                    mb: 0.4,
                    color: "#2c3e50",
                    letterSpacing: 0.2,
                    textAlign: "center",
                    textTransform: "uppercase",
                    "& strong": {
                      textDecoration: "underline",
                      textDecorationThickness: "1px",
                      textUnderlineOffset: "2px",
                      textDecorationSkipInk: "auto",
                    },
                  }}
                >
                  * Remarks & Academic Details *
                </Typography> */}

                {/* ===== Remarks Section ===== */}
                <Box sx={{ mt: 3, mb: 0.5, width: "100%", display: "block", paddingLeft: "8px" }}>
                  <Box
                    sx={{
                      fontSize: 13,
                    }}
                  >
                    <Typography
                      sx={{ mb: 0.4,lineHeight: 1.6, fontSize: 17, color: "#2c3e50" }}
                      component="div"
                      dangerouslySetInnerHTML={{
                        __html: (() => {
                          // Get values from student data
                          const studentNameValue = getStudentField("studentName");
                          const classNameValue = getStudentField("className") || (studentData as any)?.schoolClass?.name || (student as any)?.schoolClass?.name;
                          const sessionYearValue = (studentData as any)?.session?.name || studentData?.sessionName || (student as any)?.session?.name || (student as any)?.sessionName;
                          const mediumValue = getStudentField("medium") || studentData?.medium || (student as any)?.medium;

                          // Get TC data values - prioritize TC data from API response
                          const totalDaysValue = tcData?.totalDays != null ? String(tcData.totalDays) : "";
                          const presentDaysValue = tcData?.presentDays != null ? String(tcData.presentDays) : "";
                          const conductValue = tcData?.behaviour || "";
                          const dateOfLeavingValue = tcData?.dateOfLeaving || "";
                          const admissionDateValue = formatDate(studentData?.admissionDate || (student as any)?.admissionDate);
                          const nextSchoolDetailsValue = tcData?.nextSchoolDetails || "";
                          const nextSchoolStatusValue = tcData?.nextSchoolStatus || "";
                          const nextClassValue = tcData?.nextClass || "";
                          
                          // Use TC data result if available, otherwise fallback to marksheet
                          let examResultValue = tcData?.result || "";
                          if (!examResultValue) {
                            const studentId = studentData?.id || (student as any)?.id || (student as any)?.studentId;
                            if (studentId && marksheets.length > 0) {
                              const studentMarksheets = marksheets.filter((marksheet: any) => {
                                const marksheetStudentId = marksheet.studentId || marksheet.student?.id;
                                const matchesStudent = marksheetStudentId === studentId;
                                const matchesSession = !sessionYearValue || marksheet.sessionYear === sessionYearValue;
                                return matchesStudent && matchesSession;
                              });
                              
                              const finalMarksheet = studentMarksheets.find((m: any) => m.marksheetType === "BOARD") || 
                                studentMarksheets.find((m: any) => m.marksheetType === "ANNUAL") || 
                                studentMarksheets.find((m: any) => m.marksheetType === "HALF_YEARLY") || 
                                studentMarksheets[0];
                              
                              if (finalMarksheet && finalMarksheet.result) {
                                examResultValue = finalMarksheet.result;
                              }
                            }
                          }
                          
                          // Use TC data resultClass if available, otherwise use student's current class
                          const resultClassValue = tcData?.resultClass || classNameValue;
                          
                          // Use TC data yearOfResult if available, otherwise use session year
                          const resultYearValue = tcData?.yearOfResult || sessionYearValue;
                          
                          // Values with bold and underline styling - Use TC data when available
                          const values: Record<string, string> = {
                            studentName: `<strong>${getValueOrUnderline(studentNameValue)}</strong>`,
                            examResult: `<strong>${getValueOrUnderline(examResultValue)}</strong>`,
                            className: `<strong>${getValueOrUnderline(resultClassValue)}</strong>`,
                            sessionYear: `<strong>${getValueOrUnderline(resultYearValue)}</strong>`,
                            medium: `<strong>${getValueOrUnderline(mediumValue)}</strong>`,
                            totalWorkingDays: `<strong>${getValueOrUnderline(totalDaysValue)}</strong>`,
                            daysAttended: `<strong>${getValueOrUnderline(presentDaysValue)}</strong>`,
                            conduct: `<strong>${getValueOrUnderline(conductValue)}</strong>`,
                            admissionDate: `<strong>${getValueOrUnderline(admissionDateValue)}</strong>`,
                            leavingDate: `<strong>${getValueOrUnderline(dateOfLeavingValue)}</strong>`,
                            nextClass: `<strong>${getValueOrUnderline(nextClassValue)}</strong>`,
                            nextSchoolDetails: `<strong>${getValueOrUnderline(nextSchoolDetailsValue)}</strong>`,
                            nextSchoolStatus: `<strong>${getValueOrUnderline(nextSchoolStatusValue)}</strong>`,
                          };

                          // Get the translation string
                          let remarksText = t("tc_remarks_section");

                          // Replace placeholders
                          remarksText = remarksText
                            .replace(/\{studentName\}/g, values.studentName)
                            .replace(/\{examResult\}/g, values.examResult)
                            .replace(/\{className\}/g, values.className)
                            .replace(/\{sessionYear\}/g, values.sessionYear)
                            .replace(/\{medium\}/g, values.medium)
                            .replace(/\{totalWorkingDays\}/g, values.totalWorkingDays)
                            .replace(/\{daysAttended\}/g, values.daysAttended)
                            .replace(/\{conduct\}/g, values.conduct)
                            .replace(/\{admissionDate\}/g, values.admissionDate)
                            .replace(/\{leavingDate\}/g, values.leavingDate);

                          return remarksText;
                        })(),
                      }}
                    />
                  </Box>
                </Box>

                {/* ===== Signature Section ===== */}
                <Typography
                  sx={{
                    p: 0.8,
                    minHeight: "25mm",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    mt: 10,
                  }}
                >
                  {/* Class Teacher on the left */}
                  <Typography sx={{ display: "flex", flexDirection: "column", alignItems: "center", pl: 5 }}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: "bold",
                        color: "#2c3e50",
                        mb: 0.3,
                      }}
                    >
                      {getClassTeacherName()}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: "bold",
                        color: "#34495e",
                        mb: 0.4,
                      }}
                    >
                      {t("tc_class_teacher")}
                    </Typography>
                  </Typography>

                  {/* Principal on the right */}
                  <Typography sx={{ display: "flex", flexDirection: "column", alignItems: "center", pr: 5 }}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: "bold",
                        color: "#2c3e50",
                        mb: 0.3,
                      }}
                    >
                      {currentSchool?.schoolPrincipalName?.toUpperCase() ||
                        "Principal Name"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: "bold",
                        color: "#34495e",
                        mb: 0.4,
                      }}
                    >
                      {t("tc_principal")}
                    </Typography>
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
        </Box>

        {/* ===== Download Buttons (outside border) ===== */}
        {!hidePrintButton && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: {xs: 2, sm: 5},
              mt: 3, // slightly closer to the black box
              mb: 4,
              width: "100%",
              flexWrap: "wrap",
              overflowX: { xs: "hidden", sm: "visible" },
              px: { xs: 2, sm: 0 },
            }}
          >
            <Button
              variant="contained"
              startIcon={
                isDownloadingImage ? (
                  <CircularProgress size={20} />
                ) : (
                  <ImageIcon />
                )
              }
              onClick={downloadAsImage}
              disabled={isDownloadingImage || isDownloadingPdf}
              sx={{
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#1565c0" },
                minWidth: { xs: "100%", sm: "180px" },
                maxWidth: "100%",
              }}
            >
              {isDownloadingImage ? "Downloading..." : "Download as Image"}
            </Button>

            <Button
              variant="contained"
              startIcon={
                isDownloadingPdf ? <CircularProgress size={20} /> : <Download />
              }
              onClick={downloadAsPdfFromImage}
              disabled={isDownloadingImage || isDownloadingPdf}
              sx={{
                backgroundColor: "#d32f2f",
                "&:hover": { backgroundColor: "#c62828" },
                minWidth: { xs: "100%", sm: "180px" },
                maxWidth: "100%",
              }}
            >
              {isDownloadingPdf ? "Downloading..." : "Download as PDF"}
            </Button>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
});

TcTemplate.displayName = "TcTemplate";
export default TcTemplate;
